import { useState, useEffect } from 'react'; // Import useEffect
import { atom, useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { Auth } from 'aws-amplify';
import { loadCustomAttributes } from '../Helpers/cognitoAttributes';

// Define atoms for each user attribute
export const isBetaAtom = atom(false);
const localeAtom = atom('en-gb');
const zoneInfoAtom = atom('');
const ibAdminCodeAtom = atom('');
const ibTimeOffsetAtom = atom('');
const hasAnalyserAtom = atom(false);
const schoolNameAtom = atomWithStorage('school-name', '');
export const isTrialAtom = atom(false);
export const validUntilAtom = atom(null);
export const subscriptionAlertsSeenAtom = atomWithStorage('subscription-alerts-seen', '');

const useUserData = () => {
    const [isBeta, setIsBeta] = useAtom(isBetaAtom);
    const [locale, setLocale] = useAtom(localeAtom);
    const [zoneInfo, setZoneInfo] = useAtom(zoneInfoAtom);
    const [ibAdminCode, setIbAdminCode] = useAtom(ibAdminCodeAtom);
    const [ibTimeOffset, setIbTimeOffset] = useAtom(ibTimeOffsetAtom);
    const [hasAnalyser, setHasAnalyser] = useAtom(hasAnalyserAtom);
    const [schoolName, setSchoolName] = useAtom(schoolNameAtom); // Keep this line
    const [isTrial, setIsTrial] = useAtom(isTrialAtom);
    const [validUntil, setValidUntil] = useAtom(validUntilAtom);
    const [alertsSeen, setAlertsSeen] = useAtom(subscriptionAlertsSeenAtom);
    const [isLoading, setIsLoading] = useState(false);

    // Corrected Migration logic using Jotai setter
    useEffect(() => {
        const oldSchoolNameValue = localStorage.getItem('SCHOOL_NAME');
        // Check the value directly from the atom state AFTER potential initialization
        if (oldSchoolNameValue && !schoolName) {
            // If old key exists in localStorage and the atom's state is empty,
            // update the atom. This will also update 'school-name' in localStorage.
            setSchoolName(oldSchoolNameValue);
            // Remove the old key to prevent re-migration and clean up
            localStorage.removeItem('SCHOOL_NAME');
            console.log('Migrated school name from SCHOOL_NAME to school-name using Jotai setter.');
        }
    }, [schoolName, setSchoolName]); // Depend on schoolName state and setter

    const fetchUserData = async () => {
        setIsLoading(true);
        try {
            const user = await Auth.currentAuthenticatedUser();
            const attributes = await loadCustomAttributes();
            const groups = user.signInUserSession.accessToken.payload['cognito:groups'] || [];

            const isUserInBeta = groups.includes('beta');
            setIsBeta(isUserInBeta);
            setLocale(attributes.locale || 'en-gb');
            setZoneInfo(attributes.zoneinfo || '');
            setIbAdminCode(attributes.ibAdminCode || '');
            setIbTimeOffset(attributes.ibTimeOffset || '');
            setHasAnalyser(attributes.hasAnalyser === 'true');

            // Only update school name from Cognito if it exists
            const cognitoSchoolName = attributes.schoolName;
            if (cognitoSchoolName) {
                setSchoolName(cognitoSchoolName);
            } else if (schoolName) {
                // If we have a locally stored school name but not in Cognito,
                // update Cognito with our local value
                await Auth.updateUserAttributes(user, {
                    'custom:schoolName': schoolName
                });
            }

            setIsTrial(attributes.isTrial === 'true');
            if (attributes.validUntil) {
                setValidUntil(new Date(attributes.validUntil));
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
            resetUserData();
        } finally {
            setIsLoading(false);
        }
    };

    const resetUserData = () => {
        setIsBeta(false);
        setLocale('en-gb');
        setZoneInfo('');
        setIbAdminCode('');
        setIbTimeOffset('');
        setHasAnalyser(false);
        setSchoolName('');
        setIsTrial(false);
        setValidUntil(null);
    };

    const checkAnalyserAccess = async () => {
        try {
            const attributes = await loadCustomAttributes();
            return attributes.hasAnalyser === 'true';
        } catch (error) {
            console.error('Error checking analyser access:', error);
            return false;
        }
    };

    return {
        isBeta,
        locale,
        zoneInfo,
        ibAdminCode,
        ibTimeOffset,
        hasAnalyser,
        schoolName,
        isTrial,
        validUntil,
        alertsSeen,
        setAlertsSeen,
        fetchUserData,
        checkAnalyserAccess,
        setIbAdminCode,
        setZoneInfo,
        setIbTimeOffset,
        setSchoolName,
        isLoading
    };
};

export default useUserData;
